import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import {
    FaFolder, FaExternalLinkAlt, FaDownload, FaSync, FaExclamationTriangle, FaEye, FaEdit, FaTrash, FaSearch, FaTimes,
} from 'react-icons/fa';
import { useAuth } from '../AuthContext';
import { rootUrl } from '../../../config/config';
import DriveUploadZone from '../../common/DriveUploadZone';
import ConfirmModal from '../../common/ConfirmModal';
import DocumentChecklist from './DocumentChecklist';
import DocumentEditModal from './DocumentEditModal';
import DocumentPreviewModal from './DocumentPreviewModal';
import {
    FileIcon, StatusBadge, formatDate, formatFileSize, isGoogleAppsFile, isPreviewable,
    apiErrorMessage, blobApiErrorMessage, selectPortalProps,
} from './documentHelpers';

const POLL_INTERVAL_MS = 5000;
const MAX_POLLS = 24;
const UNTAGGED = '__untagged__';

const SORT_OPTIONS = [
    { value: 'newest', label: 'Newest first' },
    { value: 'oldest', label: 'Oldest first' },
    { value: 'name', label: 'Name A–Z' },
    { value: 'expiry', label: 'Expiry (soonest)' },
];

function sortFiles(files, sort) {
    const sorted = [...files];
    switch (sort) {
        case 'oldest':
            return sorted.sort((a, b) => String(a.modifiedTime).localeCompare(String(b.modifiedTime)));
        case 'name':
            return sorted.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
        case 'expiry':
            return sorted.sort((a, b) => {
                if (!a.expiry_date) return b.expiry_date ? 1 : 0;
                if (!b.expiry_date) return -1;
                return a.expiry_date.localeCompare(b.expiry_date);
            });
        default:
            return sorted.sort((a, b) => String(b.modifiedTime).localeCompare(String(a.modifiedTime)));
    }
}

/**
 * Documents tab shared by cars and drivers. The server resolves the Drive folder from
 * the entity, so this component only ever names the car or driver, never a folder.
 *
 * entityType  — "car" | "driver"
 * entityKey   — car plate number, or driver id
 */
export default function EntityDocs({ entityType, entityKey }) {
    const { token } = useAuth();
    const baseUrl = `${rootUrl}/api/entity-documents/${entityType}/${encodeURIComponent(entityKey)}`;
    const authHeader = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [actionError, setActionError] = useState('');
    const [retrying, setRetrying] = useState(false);
    const [polls, setPolls] = useState(0);

    const [uploadTypeId, setUploadTypeId] = useState(null);
    const [uploadExpiry, setUploadExpiry] = useState('');

    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState(null);
    const [sort, setSort] = useState('newest');

    const [downloading, setDownloading] = useState(null);
    const [previewFile, setPreviewFile] = useState(null);
    const [editFile, setEditFile] = useState(null);
    const [deleteFile, setDeleteFile] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    const uploadSectionRef = useRef(null);

    const fetchOverview = useCallback(async ({ quiet = false } = {}) => {
        if (!quiet) setLoading(true);
        try {
            const res = await axios.get(baseUrl, { headers: authHeader });
            setData(res.data?.data || null);
            setLoadError('');
        } catch (err) {
            const message = apiErrorMessage(err, 'Failed to load documents.');
            if (quiet) setActionError(message);
            else setLoadError(message);
        } finally {
            if (!quiet) setLoading(false);
        }
    }, [baseUrl, authHeader]);

    useEffect(() => {
        setPolls(0);
        fetchOverview();
    }, [fetchOverview]);

    // While Drive is still creating the folder, check back on our own instead of asking for a refresh.
    const folderPending = data && !data.folder.ready && data.folder.sync_status !== 'failed';
    useEffect(() => {
        if (!folderPending || polls >= MAX_POLLS) return undefined;
        const timer = setTimeout(() => {
            setPolls(p => p + 1);
            fetchOverview({ quiet: true });
        }, POLL_INTERVAL_MS);
        return () => clearTimeout(timer);
    }, [folderPending, polls, fetchOverview]);

    const handleRetrySync = async () => {
        setRetrying(true);
        setActionError('');
        try {
            await axios.post(`${baseUrl}/folder/retry`, {}, { headers: authHeader });
            setPolls(0);
            await fetchOverview({ quiet: true });
        } catch (err) {
            setActionError(apiErrorMessage(err, 'Could not retry the folder sync.'));
        } finally {
            setRetrying(false);
        }
    };

    const handleDownload = async (file) => {
        setDownloading(file.id);
        setActionError('');
        try {
            const res = await axios.get(`${baseUrl}/files/${encodeURIComponent(file.id)}/content`, { headers: authHeader, responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = file.name;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setActionError(await blobApiErrorMessage(err, `Could not download ${file.name}. Try opening it in Drive.`));
        } finally {
            setDownloading(null);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        setDeleteError('');
        try {
            await axios.delete(`${baseUrl}/files/${encodeURIComponent(deleteFile.id)}`, { headers: authHeader });
            setDeleteFile(null);
            fetchOverview({ quiet: true });
        } catch (err) {
            setDeleteError(apiErrorMessage(err, 'Could not delete the file. Please try again.'));
        } finally {
            setDeleting(false);
        }
    };

    const types = useMemo(() => data?.types || [], [data]);
    const uploadType = types.find(t => t.id === uploadTypeId) || null;

    const getUploadFields = useCallback(() => ({
        document_type_id: uploadTypeId,
        expiry_date: uploadType?.has_expiry ? uploadExpiry : '',
    }), [uploadTypeId, uploadType, uploadExpiry]);

    const handleUploadComplete = useCallback(() => {
        setUploadExpiry('');
        fetchOverview({ quiet: true });
    }, [fetchOverview]);

    const handleChecklistUpload = (row) => {
        setUploadTypeId(row.document_type_id);
        setUploadExpiry('');
        uploadSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const visibleFiles = useMemo(() => {
        const term = search.trim().toLowerCase();
        const filtered = (data?.files || []).filter(file => {
            if (typeFilter === UNTAGGED && file.document_type_id) return false;
            if (typeFilter && typeFilter !== UNTAGGED && file.document_type_id !== typeFilter) return false;
            if (!term) return true;
            return file.name.toLowerCase().includes(term)
                || (file.document_type_name || '').toLowerCase().includes(term)
                || (file.notes || '').toLowerCase().includes(term);
        });
        return sortFiles(filtered, sort);
    }, [data, search, typeFilter, sort]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12 text-gray-400 gap-2">
                <FaSync className="animate-spin" /> Loading documents…
            </div>
        );
    }

    if (loadError && !data) {
        return (
            <div className="py-10 text-center">
                <p className="flex items-center justify-center gap-2 text-sm text-red-500"><FaExclamationTriangle /> {loadError}</p>
                <button type="button" onClick={() => fetchOverview()} className="mt-3 inline-flex items-center gap-2 rounded-sm border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
                    <FaSync /> Try again
                </button>
            </div>
        );
    }

    const { folder } = data;
    const typeOptions = types.filter(t => t.active !== false).map(t => ({ value: t.id, label: t.name }));
    const filterOptions = [{ value: UNTAGGED, label: 'Untagged' }, ...types.map(t => ({ value: t.id, label: t.active === false ? `${t.name} (inactive)` : t.name }))];
    const untaggedCount = data.files.filter(f => !f.document_type_id).length;

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-500">
                    {data.files.length} file{data.files.length === 1 ? '' : 's'}
                    {untaggedCount > 0 && <span> · {untaggedCount} untagged</span>}
                </p>
                {folder.drive_link && (
                    <a href={folder.drive_link} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-sm text-[#FF5733] hover:underline">
                        <FaExternalLinkAlt /> Open in Drive
                    </a>
                )}
            </div>

            {actionError && (
                <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-500">
                    <FaExclamationTriangle className="mt-0.5 shrink-0" />
                    <span className="flex-1">{actionError}</span>
                    <button type="button" onClick={() => setActionError('')} aria-label="Dismiss error" className="text-red-400 hover:text-red-600"><FaTimes /></button>
                </div>
            )}

            {!folder.ready && (
                <div className="mb-6 rounded-xl border border-gray-100 px-4 py-8 text-center text-gray-500">
                    {folder.sync_status === 'failed' ? (
                        <>
                            <FaExclamationTriangle className="mx-auto mb-3 text-2xl text-red-400" />
                            <p className="font-medium">Drive folder sync failed.</p>
                            {folder.last_sync_error && <p className="mt-1 text-xs font-mono text-red-400">{folder.last_sync_error}</p>}
                        </>
                    ) : (
                        <>
                            <FaSync className={`mx-auto mb-3 text-2xl text-orange-300 ${polls < MAX_POLLS ? 'animate-spin' : ''}`} />
                            <p className="font-medium">Drive folder is being set up…</p>
                            <p className="mt-1 text-sm">
                                {polls < MAX_POLLS ? 'This page will update when it is ready.' : 'This is taking longer than usual. Retry the sync below.'}
                            </p>
                        </>
                    )}
                    <button
                        type="button"
                        onClick={handleRetrySync}
                        disabled={retrying}
                        className="mt-4 inline-flex items-center gap-2 rounded-sm bg-[#FF5733] px-4 py-2 text-sm font-medium text-white hover:bg-[#ff4520] disabled:opacity-50"
                    >
                        <FaSync className={retrying ? 'animate-spin' : ''} /> {retrying ? 'Retrying…' : 'Retry folder sync'}
                    </button>
                </div>
            )}

            <DocumentChecklist checklist={data.checklist} onUploadType={handleChecklistUpload} disabled={!folder.ready} />

            {folder.ready && (
                <div ref={uploadSectionRef}>
                    <div className="mb-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div>
                            <label htmlFor={`${entityType}-upload-type`} className="mb-1 block text-sm font-medium text-gray-700">Document type</label>
                            <Select
                                inputId={`${entityType}-upload-type`}
                                value={typeOptions.find(o => o.value === uploadTypeId) || null}
                                onChange={(option) => { setUploadTypeId(option ? option.value : null); setUploadExpiry(''); }}
                                options={typeOptions}
                                isClearable
                                placeholder="Untagged"
                                {...selectPortalProps}
                            />
                        </div>
                        {uploadType?.has_expiry && (
                            <div>
                                <label htmlFor={`${entityType}-upload-expiry`} className="mb-1 block text-sm font-medium text-gray-700">Expiry date</label>
                                <input
                                    id={`${entityType}-upload-expiry`}
                                    type="date"
                                    value={uploadExpiry}
                                    onChange={(e) => setUploadExpiry(e.target.value)}
                                    className="w-full rounded border border-gray-300 px-3 py-[7px] text-sm focus:border-[#FF5733] focus:outline-none"
                                />
                            </div>
                        )}
                    </div>
                    <DriveUploadZone
                        uploadUrl={`${baseUrl}/files`}
                        getExtraFields={getUploadFields}
                        onUploadComplete={handleUploadComplete}
                        hint={uploadType ? `Files will be tagged as ${uploadType.name}` : 'PDF, images, Word, Excel — up to 25 MB each'}
                    />
                </div>
            )}

            {folder.ready && data.files.length > 0 && (
                <div className="mb-3 grid grid-cols-1 gap-3 md:grid-cols-3">
                    <div>
                        <label htmlFor={`${entityType}-doc-search`} className="mb-1 block text-sm font-medium text-gray-700">Search</label>
                        <div className="relative">
                            <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400" />
                            <input
                                id={`${entityType}-doc-search`}
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Name, type or notes"
                                className="w-full rounded border border-gray-300 py-[7px] pl-8 pr-3 text-sm focus:border-[#FF5733] focus:outline-none"
                            />
                        </div>
                    </div>
                    <div>
                        <label htmlFor={`${entityType}-doc-filter`} className="mb-1 block text-sm font-medium text-gray-700">Type</label>
                        <Select
                            inputId={`${entityType}-doc-filter`}
                            value={filterOptions.find(o => o.value === typeFilter) || null}
                            onChange={(option) => setTypeFilter(option ? option.value : null)}
                            options={filterOptions}
                            isClearable
                            placeholder="All types"
                            {...selectPortalProps}
                        />
                    </div>
                    <div>
                        <label htmlFor={`${entityType}-doc-sort`} className="mb-1 block text-sm font-medium text-gray-700">Sort by</label>
                        <Select
                            inputId={`${entityType}-doc-sort`}
                            value={SORT_OPTIONS.find(o => o.value === sort)}
                            onChange={(option) => setSort(option.value)}
                            options={SORT_OPTIONS}
                            isSearchable={false}
                            {...selectPortalProps}
                        />
                    </div>
                </div>
            )}

            {folder.ready && (data.files.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                    <FaFolder className="mx-auto mb-3 text-3xl text-yellow-300" />
                    <p className="font-medium">No files yet.</p>
                    <p className="text-sm mt-1">Upload a file above to get started.</p>
                </div>
            ) : visibleFiles.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-400">No files match your search.</p>
            ) : (
                <div className="bg-white rounded-xl border border-gray-100 overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-100">
                                <th className="text-left py-2.5 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider">Name</th>
                                <th className="text-left py-2.5 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider hidden md:table-cell">Expiry</th>
                                <th className="text-left py-2.5 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider hidden lg:table-cell">Uploaded by</th>
                                <th className="text-left py-2.5 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider hidden sm:table-cell">Modified</th>
                                <th className="text-right py-2.5 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider hidden sm:table-cell">Size</th>
                                <th className="py-2.5 px-4"><span className="sr-only">Actions</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleFiles.map((file) => {
                                const googleApps = isGoogleAppsFile(file.mimeType);
                                const canPreview = isPreviewable(file.mimeType);
                                return (
                                    <tr key={file.id} className="border-b border-gray-50 hover:bg-gray-50 last:border-0 align-top">
                                        <td className="py-3 px-4">
                                            <div className="flex items-start gap-2.5">
                                                <span className="mt-0.5 text-base shrink-0"><FileIcon mimeType={file.mimeType} /></span>
                                                <div className="min-w-0">
                                                    <button
                                                        type="button"
                                                        onClick={() => (canPreview ? setPreviewFile(file) : window.open(file.webViewLink, '_blank', 'noreferrer'))}
                                                        className="block max-w-[180px] truncate text-left font-medium text-gray-800 hover:text-[#FF5733] sm:max-w-xs"
                                                        title={file.name}
                                                    >
                                                        {file.name}
                                                    </button>
                                                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                                        {file.document_type_name ? (
                                                            <span className="rounded bg-orange-50 px-1.5 py-0.5 text-xs text-[#FF5733]">{file.document_type_name}</span>
                                                        ) : (
                                                            <button type="button" onClick={() => setEditFile(file)} className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500 hover:bg-orange-50 hover:text-[#FF5733]">
                                                                Untagged · set type
                                                            </button>
                                                        )}
                                                        <span className="md:hidden">{file.expiry_status && <StatusBadge status={file.expiry_status} />}</span>
                                                    </div>
                                                    {file.notes && <p className="mt-1 text-xs text-gray-500">{file.notes}</p>}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-3 px-4 hidden md:table-cell">
                                            {file.expiry_date ? (
                                                <div className="space-y-1">
                                                    <p className="text-gray-700">{formatDate(file.expiry_date)}</p>
                                                    <StatusBadge status={file.expiry_status} />
                                                </div>
                                            ) : file.has_expiry ? (
                                                <StatusBadge status="no_expiry" />
                                            ) : <span className="text-gray-400">—</span>}
                                        </td>
                                        <td className="py-3 px-4 text-gray-500 hidden lg:table-cell">{file.uploaded_by || '—'}</td>
                                        <td className="py-3 px-4 text-gray-500 hidden sm:table-cell whitespace-nowrap">{formatDate(file.modifiedTime)}</td>
                                        <td className="py-3 px-4 text-gray-500 text-right hidden sm:table-cell whitespace-nowrap">{formatFileSize(file.size)}</td>
                                        <td className="py-3 px-4">
                                            <div className="flex items-center justify-end gap-0.5">
                                                {canPreview && (
                                                    <button type="button" onClick={() => setPreviewFile(file)} className="rounded p-1.5 text-gray-500 hover:bg-orange-50 hover:text-[#FF5733]" title="Preview" aria-label={`Preview ${file.name}`}>
                                                        <FaEye />
                                                    </button>
                                                )}
                                                {!googleApps && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDownload(file)}
                                                        disabled={downloading === file.id}
                                                        className="rounded p-1.5 text-gray-500 hover:bg-orange-50 hover:text-[#FF5733] disabled:opacity-50"
                                                        title="Download"
                                                        aria-label={`Download ${file.name}`}
                                                    >
                                                        <FaDownload className={downloading === file.id ? 'animate-bounce' : ''} />
                                                    </button>
                                                )}
                                                <button type="button" onClick={() => setEditFile(file)} className="rounded p-1.5 text-gray-500 hover:bg-orange-50 hover:text-[#FF5733]" title="Edit" aria-label={`Edit ${file.name}`}>
                                                    <FaEdit />
                                                </button>
                                                <button type="button" onClick={() => { setDeleteError(''); setDeleteFile(file); }} className="rounded p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-600" title="Delete" aria-label={`Delete ${file.name}`}>
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ))}

            <DocumentPreviewModal file={previewFile} baseUrl={baseUrl} onClose={() => setPreviewFile(null)} />
            <DocumentEditModal
                file={editFile}
                types={types}
                baseUrl={baseUrl}
                onClose={() => setEditFile(null)}
                onSaved={() => { setEditFile(null); fetchOverview({ quiet: true }); }}
            />
            <ConfirmModal
                isOpen={Boolean(deleteFile)}
                title="Delete document"
                description={deleteFile ? `"${deleteFile.name}" will be moved to the Google Drive trash, where it can be restored for 30 days.` : ''}
                onCancel={() => !deleting && setDeleteFile(null)}
                onConfirm={handleDelete}
                confirmText="Delete"
                loading={deleting}
                error={deleteError}
                variant="danger"
            />
        </div>
    );
}
